import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { loginSuccess } from '../../features/auth/authSlice';
import { clearFloorPlan } from '../../features/floorplan/floorplanSlice';
import { clearLayout } from '../../features/layout/layoutSlice';
import { clearFacility } from '../../features/facility/facilitySlice';
import { clearDxfImport } from '../../features/dxfImport/dxfImportSlice';
import { loginWithPassword } from '../../utils/authApi';
import { createUser } from '../../utils/usersApi';
import { PoweredByPathfinder } from '../common/PoweredByPathfinder';

type AuthMode = 'login' | 'register';

const inputClassName =
  'mt-1.5 h-10 w-full rounded border border-white bg-transparent px-3.5 text-base text-white outline-none placeholder:text-[#667085] focus:border-[#fd6260] focus:ring-1 focus:ring-[#fd6260] disabled:opacity-60';

const LoginScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [mode, setMode] = useState<AuthMode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!error) return;
    submitButtonRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setError(null);
    setConfirmPassword('');
    if (next === 'login') {
      setEmail('');
      setFullName('');
    }
  };

  const completeLogin = async (
    loginUsername: string,
    loginPassword: string,
    profile?: { email?: string | null; fullName?: string | null }
  ) => {
    const result = await loginWithPassword({
      username: loginUsername,
      password: loginPassword,
    });

    dispatch(clearDxfImport());
    dispatch(clearFloorPlan());
    dispatch(clearLayout());
    dispatch(clearFacility());
    dispatch(
      loginSuccess({
        username: result.username,
        accessToken: result.accessToken,
        tokenType: result.tokenType,
        userId: result.userId,
        email: profile?.email ?? null,
        fullName: profile?.fullName ?? null,
      })
    );
    navigate('/', { replace: true });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim() || null;
    const trimmedFullName = fullName.trim() || null;
    if (!trimmedUsername || !password) {
      setError('Username and password are required.');
      return;
    }

    if (mode === 'register') {
      if (password.length < 6) {
        setError('Password must be at least 6 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setError(null);
    setIsSubmitting(true);
    try {
      if (mode === 'register') {
        const created = await createUser({
          username: trimmedUsername,
          password,
          email: trimmedEmail,
          fullName: trimmedFullName,
        });
        await completeLogin(trimmedUsername, password, {
          email: created.email ?? trimmedEmail,
          fullName: created.fullName ?? trimmedFullName,
        });
      } else {
        await completeLogin(trimmedUsername, password);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : mode === 'register'
            ? 'Registration failed. Please try again.'
            : 'Login failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-[#eff4fa] bg-[url('/assets/bg-login.png')] bg-cover bg-center bg-no-repeat">
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-4 py-10">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[456px] bg-[#373f57] px-8 py-10 text-white shadow-sm sm:px-12 sm:py-12"
      >
        <div className="flex flex-col items-center gap-6">
          <div className="text-2xl font-bold tracking-[-0.02em]">Peakaso</div>
          <h1 className="text-center text-[30px] font-semibold leading-[38px]">
            {mode === 'login' ? 'Log in to your account' : 'Create your account'}
          </h1>
        </div>

        <div className={`mt-8 ${mode === 'register' ? 'space-y-4' : 'space-y-5'}`}>
          {mode === 'register' && (
            <label className="block">
              <span className="text-sm font-medium leading-5 text-white">Full name</span>
              <input
                type="text"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                placeholder="Enter your full name"
                autoComplete="name"
                disabled={isSubmitting}
                className={inputClassName}
              />
            </label>
          )}

          <label className="block">
            <span className="text-sm font-medium leading-5 text-white">Username *</span>
            <input
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="Enter your username"
              autoComplete="username"
              disabled={isSubmitting}
              className={inputClassName}
            />
          </label>

          {mode === 'register' && (
            <label className="block">
              <span className="text-sm font-medium leading-5 text-white">Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Enter your email"
                autoComplete="email"
                disabled={isSubmitting}
                className={inputClassName}
              />
            </label>
          )}

          <label className="block">
            <span className="text-sm font-medium leading-5 text-white">Password *</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={mode === 'register' ? 'Create a password' : 'Enter your password'}
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              disabled={isSubmitting}
              className={inputClassName}
            />
          </label>

          {mode === 'register' && (
            <label className="block">
              <span className="text-sm font-medium leading-5 text-white">Confirm password *</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                disabled={isSubmitting}
                className={inputClassName}
              />
            </label>
          )}
        </div>

        {error && (
          <div className="mt-4 rounded border border-[#fd6260]/50 bg-[#fd6260]/10 px-3 py-2 text-sm text-white">
            {error}
          </div>
        )}

        <button
          ref={submitButtonRef}
          type="submit"
          disabled={isSubmitting}
          className="mt-6 flex h-10 w-full items-center justify-center rounded bg-[#fd6260] px-[18px] text-base font-semibold leading-6 text-white transition-colors hover:bg-[#f45452] focus:outline-none focus:ring-2 focus:ring-[#fd6260] focus:ring-offset-2 focus:ring-offset-[#373f57] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSubmitting
            ? mode === 'register'
              ? 'Creating account…'
              : 'Signing in…'
            : mode === 'register'
              ? 'Create account'
              : 'Sign in'}
        </button>

        <div className="mt-7 text-center text-sm leading-5">
          {mode === 'login' ? (
            <>
              <span className="text-white/70">Need an account? </span>
              <button
                type="button"
                onClick={() => switchMode('register')}
                className="font-semibold text-[#fd6260] hover:text-[#ff7876]"
              >
                Create account
              </button>
            </>
          ) : (
            <>
              <span className="text-white/70">Already have an account? </span>
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="font-semibold text-[#fd6260] hover:text-[#ff7876]"
              >
                Sign in
              </button>
            </>
          )}
        </div>

        {mode === 'login' && (
          <div className="mt-4 text-center">
            <button
              type="button"
              className="text-sm font-semibold leading-5 text-[#fd6260] hover:text-[#ff7876]"
            >
              Forgot Password?
            </button>
          </div>
        )}
      </form>
      </div>
      <footer className="flex h-8 shrink-0 items-center border-t border-slate-200 bg-white px-4 text-[11px] text-slate-500">
        <PoweredByPathfinder className="text-slate-600" />
      </footer>
    </div>
  );
};

export default LoginScreen;
